/**
 * @file roleUtils.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-06-04
 * @description module for managing private chat
 */

import { db, storage } from "../firebaseConfig";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  where,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { fetchYoutubeVideoNameFromUrl } from "./livestreamsUtils";
import {
  getContentType,
  fetchPublicUserInfo,
  logWebsiteUsage,
} from "./usersUtils";
import { getAuth } from "firebase/auth";

/**
 * Fetches all private chats for the authenticated user.
 * Sets up a real-time listener to the user's invitations collection,
 * which contains the chat IDs of the chats the user is invited to.
 * Then it sets up listeners for each chat document to keep the private chats updated in real-time.
 *
 * @param {Object} user - the authenticated user object
 * @param {Function} setPrivateChats - a function to set the private chats in the state
 * @param {Function} setSelectedPrivateChat - a function to set the selected private chat
 * @param {Function} setPrivateChatMembers - a function to set the private chat members
 * @param {Function} setSelectedPrivateChatMembers - a function to set the selected private chat members
 * @param {Function} setPendingPrivateChatMembers - a function to set the pending private chat members
 * @param {Function} setSelectedPrivateChatPendingMembers - a function to set the selected private chat pending members
 * @param {Object} chatCacheRef - a useRef variable for the chat cache, which is a Map that stores chat data, member data, and timestamps for last modification
 * @param {Object} selectedPrivateChatRef - a useRef variable for the currently selected private chat
 * @param {Function} setLoadingChatSettings - a function to set the loading state for chat settings
 *
 * @returns {Function} - a function to unsubscribe from the chat updates
 * @throws {Error} - if there is an error setting up the chat listener
 */
export const fetchChats = (
  user,
  setPrivateChats,
  setSelectedPrivateChat,
  setPrivateChatMembers,
  setSelectedPrivateChatMembers,
  setPendingPrivateChatMembers,
  setSelectedPrivateChatPendingMembers,
  chatCacheRef, //chatId -> { chatData, memberData, settingsLastUpdated, membersLastUpdated }
  selectedPrivateChatRef,
  setLoadingChatSettings, // Function to set loading state for chat settings
) => {
  try {
    console.log("Setting up chat listener for user", user.uid);

    // Query the user's privateChats subcollection
    const privateChatsQuery = query(
      collection(db, "users", user.uid, "privateChats"),
    );

    // Set up listener for the user's privateChats subcollection
    const privateChatsUnsubscribe = onSnapshot(
      privateChatsQuery,
      async (privateChatsSnapshot) => {
        console.log("Listener triggered, user's private chats updated...");

        setLoadingChatSettings(true); // Set loading state to true while setting up listener

        let chatCache = chatCacheRef.current;
        let selectedPrivateChat = selectedPrivateChatRef.current;

        console.log("selectedPrivateChat=", selectedPrivateChat);

        // If no private chats, clear everything
        if (privateChatsSnapshot.empty) {
          chatCache.clear();
          setPrivateChats([]);
          setSelectedPrivateChat(null);
          setPrivateChatMembers(new Map());
          setSelectedPrivateChatMembers(null);
          setLoadingChatSettings(false); // Reset loading state
          console.log("No private chats found for user, clearing state.");
          return;
        }

        // Get current chat references from subcollection
        const currentChatRefs = new Map(); // chatId -> {settingsLastUpdated, membersLastUpdated}

        privateChatsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          console.log("doc=", doc.id, data);

          //docId is the chatId
          currentChatRefs.set(doc.id, {
            settingsLastUpdated: data.settingsLastUpdated.toDate(), //firestore returns a timestamp even if the field is stored as a date object
            membersLastUpdated: data.membersLastUpdated.toDate(),
            pendingMembersLastUpdated: data.pendingMembersLastUpdated.toDate(),
          });
        });

        // Identify chats to fetch (new or updated settings)
        const chatsToFetch = [];
        // Identify chats that need member updates
        const membersToFetch = [];
        // Identify chats that need pending member updates
        const pendingMembersToFetch = [];

        for (const [
          chatId,
          {
            settingsLastUpdated,
            membersLastUpdated,
            pendingMembersLastUpdated,
          },
        ] of currentChatRefs) {
          const cached = chatCache.get(chatId);
          console.log("cached=", cached);

          // Check if we need to fetch chat settings
          console.log("chatRefs=", [...currentChatRefs.entries()]);
          console.log(
            `chatId = ${chatId} settingsLastUpdated = ${settingsLastUpdated} cached = ${cached?.settingsLastUpdated}`,
          );

          if (
            !cached ||
            !cached.settingsLastUpdated ||
            settingsLastUpdated > cached.settingsLastUpdated
          ) {
            chatsToFetch.push(chatId);
            console.log("chatsToFetch = ", chatsToFetch);
          } else {
            console.log("nothing to fetch here...");
          }

          // Check if we need to fetch member data
          if (
            !cached ||
            !cached.membersLastUpdated ||
            membersLastUpdated > cached.membersLastUpdated
          ) {
            membersToFetch.push(chatId);
          }

          // Check if we need to fetch pending members
          if (
            !cached ||
            !cached.pendingMembersLastUpdated ||
            pendingMembersLastUpdated > cached.pendingMembersLastUpdated
          ) {
            pendingMembersToFetch.push(chatId);
          }
        }

        // Remove chats that are no longer in user's privateChats
        const currentChatIds = new Set(currentChatRefs.keys());
        for (const cachedChatId of chatCache.keys()) {
          if (!currentChatIds.has(cachedChatId)) {
            console.log(`Removing chat ${cachedChatId} from cache`);
            chatCache.delete(cachedChatId);
          }
        }

        // Fetch chat settings that need updating
        if (chatsToFetch.length > 0) {
          console.log(
            `Fetching ${chatsToFetch.length} updated chats:`,
            chatsToFetch,
          );

          const chatPromises = chatsToFetch.map((chatId) =>
            getDoc(doc(db, "privateChats", chatId)),
          );

          const chatSnapshots = await Promise.all(chatPromises);

          // Update cache with new chat data
          chatSnapshots.forEach((chatSnap) => {
            console.log("updating cache...");

            if (chatSnap.exists()) {
              const chatId = chatSnap.id;
              const chatData = { ...chatSnap.data() };
              const { settingsLastUpdated } = currentChatRefs.get(chatId);

              // Update or create cache entry
              const existingCache = chatCache.get(chatId) || {};
              chatCache.set(chatId, {
                ...existingCache,
                chatData,
                settingsLastUpdated,
              });
            }
          });
        }

        // Fetch member data that needs updating
        if (membersToFetch.length > 0) {
          console.log(
            `Fetching ${membersToFetch.length} member updates:`,
            membersToFetch,
          );

          for (const chatId of membersToFetch) {
            try {
              // Query the members subcollection for this chat
              const members = collection(db, "privateChats", chatId, "members");

              const membersSnapshot = await getDocs(members);

              const memberData = [];

              if (!membersSnapshot.empty) {
                // Get member data directly from subcollection documents
                membersSnapshot.docs.forEach((doc) => {
                  memberData.push({
                    uid: doc.id,
                    ...doc.data(),
                  });
                });
              } else {
                console.warn(`No members found for chat ${chatId}`);
              }

              console.log("memberData=", memberData);

              // Update cache with member data
              const existingCache = chatCache.get(chatId) || {};
              const { membersLastUpdated } = currentChatRefs.get(chatId);

              chatCache.set(chatId, {
                ...existingCache,
                memberData,
                membersLastUpdated,
              });
            } catch (error) {
              console.error(
                `Error fetching members for chat ${chatId}:`,
                error,
              );
            }
          }
        }

        // Fetch pending members that need updating
        if (pendingMembersToFetch.length > 0) {
          console.log(
            `Fetching ${pendingMembersToFetch.length} pending members:`,
            pendingMembersToFetch,
          );

          for (const chatId of pendingMembersToFetch) {
            try {
              // Query the members subcollection for this chat
              const pendingMembersQuery = query(
                collection(db, "privateChats", chatId, "invitations"),
                where("status", "==", "pending"),
              );

              let pendingMembersSnapshot = await getDocs(pendingMembersQuery);

              const pendingMemberIds = [];
              const pendingMemberData = [];

              if (!pendingMembersSnapshot.empty) {
                // Get member data directly from subcollection documents
                pendingMembersSnapshot.docs.forEach((doc) => {
                  pendingMemberIds.push(doc.data().userId);
                });

                //retrieve user info from publicUserInfo using the pendingMemberIds
                await Promise.all(
                  pendingMemberIds.map(async (uid) => {
                    const userInfo = await getDoc(
                      doc(db, "publicUserInfo", uid),
                    );
                    if (userInfo.exists()) {
                      pendingMemberData.push({
                        uid,
                        ...userInfo.data(),
                        membershipStatus: "pending",
                      });
                    }
                  }),
                );
              } else {
                console.warn(`No members found for chat ${chatId}`);
              }

              console.log("memberData=", pendingMemberData);

              // Update cache with member data
              const existingCache = chatCache.get(chatId) || {};
              const { pendingMembersLastUpdated } = currentChatRefs.get(chatId);

              chatCache.set(chatId, {
                ...existingCache,
                pendingMemberData: pendingMemberData,
                pendingMembersLastUpdated,
              });
            } catch (error) {
              console.error(
                `Error fetching pending members for chat ${chatId}:`,
                error,
              );
            }
          }
        }

        // Build final chat list from cache (only chats that exist in user's privateChats)
        const validChats = [];
        const membersMap = new Map();
        const pendingMembersMap = new Map();

        for (const chatId of currentChatIds) {
          const cached = chatCache.get(chatId);
          if (cached && cached.chatData) {
            validChats.push({ id: chatId, ...cached.chatData });

            // Add members to the map if available
            if (cached.memberData) {
              membersMap.set(chatId, cached.memberData);
            }

            if (cached.pendingMemberData) {
              pendingMembersMap.set(chatId, cached.pendingMemberData);
            }
          }
        }

        console.log("Updated private chats:", validChats);
        console.log("Updated chat members:", membersMap);
        console.log("Updated pending chat members:", pendingMembersMap);

        setPrivateChats(validChats);
        setPrivateChatMembers(membersMap);
        setPendingPrivateChatMembers(pendingMembersMap);

        // Update selected chat if it still exists and was updated
        if (selectedPrivateChat) {
          console.log(
            "detected private chat selected, updating data... current priv chat =",
            selectedPrivateChat,
          );

          const selectedChatId = selectedPrivateChat.id;
          console.log(
            "current chat Ids =",
            currentChatIds,
            "selectedChatId=",
            selectedChatId,
          );

          if (currentChatIds.has(selectedChatId)) {
            const cachedChat = chatCache.get(selectedChatId); //chat cache already has the latest version of all chats.
            console.log("cachedChat=", cachedChat);

            if (cachedChat) {
              // Update chat settings if needed
              if (cachedChat.chatData) {
                console.log(
                  "Updating selected chat, new data =",
                  cachedChat.chatData,
                );
                setSelectedPrivateChat({
                  id: selectedChatId,
                  ...cachedChat.chatData,
                });
              }
            }

            if (cachedChat.memberData) {
              setSelectedPrivateChatMembers(cachedChat.memberData);
            }

            if (cachedChat.pendingMemberData) {
              setSelectedPrivateChatPendingMembers(
                cachedChat.pendingMemberData,
              );
            }
          } else {
            console.log(
              "currentChat does not exist. Resetting state. (removed by owner).",
            );
            setSelectedPrivateChat(null);
            setSelectedPrivateChatMembers([]);
          }
        } else {
          console.log("not changing anything");
        }

        setLoadingChatSettings(false);
      },
    );

    // Return cleanup function
    return () => {
      privateChatsUnsubscribe();
    };
  } catch (error) {
    console.error("Error setting up chat listener:", error);
    setPrivateChats([]);
    setSelectedPrivateChat(null);
    setPrivateChatMembers(new Map());
    return () => {};
  }
};

/**
 * allows user who calls function to leave private chat
 *
 * @param {string} privateChatId - the ID of the private chat
 *
 * @returns {Promise<void>} - a promise that resolves when the user has left the chat
 */
export const leavePrivateChat = async (privateChatId) => {
  try {
    const auth = getAuth();
    const userId = auth.currentUser.uid;

    const chatRef = doc(db, "privateChats", privateChatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
      console.error("Chat document does not exist.");
      return;
    }

    const chatData = chatSnap.data();

    //get data of members
    const memberRef = collection(db, "privateChats", privateChatId, "members");
    const memberSnap = await getDocs(memberRef);

    if (memberSnap.empty) {
      console.error("No members found in the chat.");
      return;
    }

    const memberData = memberSnap.docs.map((doc) => ({
      ...doc.data(),
      uid: doc.id,
    }));

    //determine owner
    const owner = memberData.find(
      ({ membershipStatus }) => membershipStatus === "owner",
    );
    if (!owner) {
      console.error("No owner found in the chat.");
      return;
    }

    //determine regular members
    let currentMembers = memberData.filter(
      ({ membershipStatus }) => membershipStatus === "member",
    );

    const currentTime = new Date();

    if (owner.uid === userId) {
      //no members, to make new owner so delete chat
      if (!currentMembers || currentMembers.length === 0) {
        //fetch invitations of private chat
        const invitationsRef = collection(
          db,
          "privateChats",
          privateChatId,
          "invitations",
        );
        const invitationsSnap = await getDocs(invitationsRef);

        let invitationsData = null;
        if (!invitationsSnap.empty) {
          invitationsData = invitationsSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          //set chatStatus of all invitations to "chatDeleted" for user documents
          await Promise.all(
            invitationsData.map(({ userId, userInvitationId }) =>
              handleCancelInvitation(userId, userInvitationId),
            ),
          );
        }

        //fetch messages of private chat
        const messagesRef = collection(
          db,
          "privateChats",
          privateChatId,
          "messages",
        );
        const messagesSnap = await getDocs(messagesRef);

        let messagesData = null;
        if (!messagesSnap.empty) {
          messagesData = messagesSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        }

        //fetch deleted messages of private chat
        const delMessagesRef = collection(
          db,
          "privateChats",
          privateChatId,
          "deletedMessages",
        );
        const delMessagesSnap = await getDocs(delMessagesRef);

        let delMessagesData = null;
        if (!delMessagesSnap.empty) {
          delMessagesData = delMessagesSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        }

        //fetch private chat activity of private chat
        const activityRef = collection(
          db,
          "privateChats",
          privateChatId,
          "privateChatActivity",
        );
        const activitySnap = await getDocs(activityRef);

        let activityData = null;
        if (!activitySnap.empty) {
          activityData = activitySnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        }

        //archive chat in deletePrivateChats collection
        const deletedRef = doc(db, "deletedPrivateChats", privateChatId);
        await setDoc(deletedRef, {
          ...chatData,
          deletedAt: currentTime,
        });

        //add invitations to archive
        if (invitationsData) {
          const deleteRefInvitations = collection(deletedRef, "invitations");
          await Promise.all(
            invitationsData.map(async (inv) => {
              return setDoc(doc(deleteRefInvitations, inv.id), {
                ...inv,
              });
            }),
          );

          //delete original invitations
          const deleteInvitations = invitationsSnap.docs.map((doc) =>
            deleteDoc(doc.ref),
          );
          await Promise.all(deleteInvitations);
        }

        //add messages to archive
        if (messagesData) {
          const deleteRefMessages = collection(deletedRef, "messages");

          await Promise.all(
            messagesData.map(async (message) => {
              return setDoc(doc(deleteRefMessages, message.id), message);
            }),
          );

          //delete original messages
          const deleteMessages = messagesSnap.docs.map((doc) =>
            deleteDoc(doc.ref),
          );
          await Promise.all(deleteMessages);
        }

        //add deleted messages to archive
        if (delMessagesData) {
          const deleteRefDelMessages = collection(
            deletedRef,
            "deletedMessages",
          );

          await Promise.all(
            delMessagesData.map(async (delMessage) => {
              return setDoc(
                doc(deleteRefDelMessages, delMessage.id),
                delMessage,
              );
            }),
          );

          //delete original deleted messages
          const deleteDelMessages = delMessagesSnap.docs.map((doc) =>
            deleteDoc(doc.ref),
          );
          await Promise.all(deleteDelMessages);
        }

        //add privateChatActivity to archive
        if (activityData) {
          const deleteRefActivity = collection(
            deletedRef,
            "privateChatActivity",
          );
          await Promise.all(
            activityData.map(async (activity) => {
              return setDoc(doc(deleteRefActivity, activity.id), activity);
            }),
          );

          //delete original activity
          const deleteActivity = activitySnap.docs.map((doc) =>
            deleteDoc(doc.ref),
          );
          await Promise.all(deleteActivity);
        }

        //add members to archive
        if (memberData) {
          /*
           * no point in moving members over to archive, because everytime someone leaves their doc is removed from the members collection, so at this point the members collection will be empty aside from owner.
           * the way we track history member history will be from logs
           */

          //delete original members
          const deleteMembers = memberSnap.docs.map((doc) =>
            deleteDoc(doc.ref),
          );
          await Promise.all(deleteMembers);
        }

        //delete original chat
        await deleteDoc(chatRef);
        console.log("deleted chat successfully");

        //delete chat from user's privateChats array
        const userChatsRef = doc(
          db,
          "users",
          userId,
          "privateChats",
          privateChatId,
        );
        await deleteDoc(userChatsRef);

        //now update user history.
        logWebsiteUsage(
          userId,
          `User left private chat with ID '${privateChatId}'.`,
          "privateChatMembershipHistory",
          currentTime,
        );

        //must write to the deletedPrivateChatCollection
        logDeletedPrivateChatActivity(
          privateChatId,
          `User with ID '${userId}' left the chat. They were the last remaining member. Chat is no longer active.`,
          currentTime,
        );
      }
      //members exist, so make one of them the new owner
      else {
        //take first member as new owner
        const newOwnerDocId = currentMembers[0].uid;
        updateDoc(
          doc(db, "privateChats", privateChatId, "members", newOwnerDocId),
          {
            membershipStatus: "owner",
          },
        );

        //delete document of user from private chats
        deleteDoc(doc(db, "privateChats", privateChatId, "members", owner.uid));

        //dont update currentMembers to remove new owner, because we want to update their membersLastUpdated field

        //delete chat from user's privateChats array
        const userChatRef = doc(
          db,
          "users",
          userId,
          "privateChats",
          privateChatId,
        );
        deleteDoc(userChatRef);

        //update members' privateChats subcollection
        currentMembers.forEach(async (member) => {
          const memberChatRef = doc(
            db,
            "users",
            member.uid,
            "privateChats",
            privateChatId,
          );
          const memberChatSnap = await getDoc(memberChatRef);
          if (!memberChatSnap.exists()) {
            console.error(
              `No private chat found for member with UID ${member.uid} and chatId ${privateChatId}`,
            );
            return;
          }
          await updateDoc(memberChatRef, {
            membersLastUpdated: currentTime,
          });
        });

        //log in user history
        logWebsiteUsage(
          userId,
          `User left private chat with ID '${privateChatId}'.`,
          "privateChatMembershipHistory",
          currentTime,
        );

        //log in chat activity
        logPrivateChatActivity(
          privateChatId,
          `User with ID '${userId}' left the chat. They were the owner; new owner has ID '${newOwnerDocId}'. Chat remains active.`,
          currentTime,
        );
      }
      //user is a member, so just delete them from members subcollection
    } else if (currentMembers.some(({ uid }) => uid === userId)) {
      deleteDoc(doc(db, "privateChats", privateChatId, "members", userId));

      currentMembers = currentMembers.filter((member) => member.uid !== userId);

      //delete chat from user's privateChats array
      const userChatsRef = doc(
        db,
        "users",
        userId,
        "privateChats",
        privateChatId,
      );
      const userChatDoc = await getDoc(userChatsRef);
      if (userChatDoc.exists()) {
        await deleteDoc(userChatDoc.ref);
        console.log("deleted chat from user's privateChats array");
      } else {
        console.error("User's private chat not found in privateChats array.");
      }

      //update members' privateChats subcollection
      currentMembers.forEach(async (member) => {
        const memberChatRef = doc(
          db,
          "users",
          member.uid,
          "privateChats",
          privateChatId,
        );
        const memberChatSnap = await getDoc(memberChatRef);
        if (!memberChatSnap.exists()) {
          console.error(
            `No private chat found for member with UID ${member.uid} and chatId ${privateChatId}`,
          );
          return;
        }
        await updateDoc(memberChatRef, {
          membersLastUpdated: currentTime,
        });
      });

      //update owner's privateChats subcollection
      const ownerChatRef = doc(
        db,
        "users",
        owner.uid,
        "privateChats",
        privateChatId,
      );
      const ownerChatSnap = await getDoc(ownerChatRef);
      if (!ownerChatSnap.exists()) {
        console.error(
          `No private chat found for owner with UID ${owner.uid} and chatId ${privateChatId}`,
        );
        return;
      }
      await updateDoc(ownerChatRef, {
        membersLastUpdated: currentTime,
      });

      //log on user history
      logWebsiteUsage(
        userId,
        `User left private chat with ID '${privateChatId}'.`,
        "privateChatMembershipHistory",
        currentTime,
      );

      //log in chat activity
      logPrivateChatActivity(
        privateChatId,
        `User with ID '${userId}' left the private chat. They were a member. Chat remains active.`,
        currentTime,
      );
    } else {
      console.log("user is not an owner or member of chat");
    }
  } catch (error) {
    console.error("Error leaving private chat:", error);
  }
};

/**
 * allows user to leave private chat
 *
 * @param {object} userToRemove - user ID to remove from chat
 * @param {string} privateChatId - the ID of the private chat
 *
 * @returns {Promise<void>} - a promise that resolves when the user has left the chat
 */
export const removePrivateChatMember = async (userToRemove, privateChatId) => {
  try {
    const authUser = getAuth();
    const callerId = authUser.currentUser.uid;

    const chatRef = doc(db, "privateChats", privateChatId);
    const chatSnap = await getDoc(chatRef);

    console.log("snap=", chatSnap);

    if (!chatSnap.exists()) {
      console.error("Chat document does not exist.");
      return;
    }

    //get data of members
    const memberRef = collection(db, "privateChats", privateChatId, "members");
    const memberSnap = await getDocs(memberRef);

    if (memberSnap.empty) {
      console.error("No members found in the chat.");
      return;
    }

    const memberData = memberSnap.docs.map((doc) => ({
      ...doc.data(),
      uid: doc.id,
    }));

    console.log("memberData=", memberData);

    //determine owner
    const owner = memberData.find(
      ({ membershipStatus }) => membershipStatus === "owner",
    );
    if (!owner) {
      console.error("No owner found in the chat.");
      return;
    }

    //determine regular members
    let currentMembers = memberData.filter(
      ({ membershipStatus }) => membershipStatus === "member",
    );

    const currentTime = new Date();

    //can only remove if you are the owner of the chat.
    if (owner.uid === callerId) {
      if (!currentMembers.some(({ uid }) => uid === userToRemove)) {
        console.error("User to remove is not a current member, cannot remove.");
        return;
      }

      deleteDoc(
        doc(db, "privateChats", privateChatId, "members", userToRemove),
      );

      //delete chat from user's privateChats array
      const userChatsRef = doc(
        db,
        "users",
        userToRemove,
        "privateChats",
        privateChatId,
      );

      const userChatDoc = await getDoc(userChatsRef);
      if (userChatDoc.exists()) {
        await deleteDoc(userChatDoc.ref);
        console.log("deleted chat from user's privateChats array");
      } else {
        console.error("User's private chat not found in privateChats array.");
      }

      //update members' privateChats subcollection
      currentMembers.forEach(async (member) => {
        const memberChatRef = doc(
          db,
          "users",
          member.uid,
          "privateChats",
          privateChatId,
        );
        const memberChatSnap = await getDoc(memberChatRef);
        if (!memberChatSnap.exists()) {
          console.error(
            `No private chat found for member with UID ${member.uid} and chatId ${privateChatId}`,
          );
          return;
        }
        await updateDoc(memberChatRef, {
          membersLastUpdated: currentTime,
        });
      });

      //update owner's privateChats subcollection
      const ownerChatRef = doc(
        db,
        "users",
        owner.uid,
        "privateChats",
        privateChatId,
      );
      const ownerChatSnap = await getDoc(ownerChatRef);
      if (!ownerChatSnap.exists()) {
        console.error(
          `No private chat found for owner with UID ${owner.uid} and chatId ${privateChatId}`,
        );
        return;
      }
      await updateDoc(ownerChatRef, {
        membersLastUpdated: currentTime,
      });

      //log on user history
      logWebsiteUsage(
        callerId,
        `User removed member with ID '${userToRemove}' from private chat with ID '${privateChatId}'.`,
        "privateChatOwnerActivity",
        currentTime,
      );

      //log in chat activity
      logPrivateChatActivity(
        privateChatId,
        `User with ID '${userToRemove}' was removed from chat by the owner with ID '${callerId}'. Chat remains active.`,
        currentTime,
      );
    } else {
      console.log(
        "user is not an owner of chat, no permission to remove any members.",
      );
    }
  } catch (error) {
    console.error("Error leaving private chat:", error);
  }
};

/**
 * Fetches messages from a private chat and sets them in the state. (The result is stored in the messages state variable)
 *
 * @param {string} privateChatId - the ID of the private chat
 * @param {Function} setMessages - a function to set the messages in the state
 *
 * @returns {Function} - a function to unsubscribe from the messages updates
 */
export const fetchPrivateChatMessages = (privateChatId, setMessages) => {
  const q = query(
    collection(db, "privateChats", privateChatId, "messages"),
    orderBy("timestamp", "asc"),
  );

  //onSnapshot will detect if there are any changes in documents matching the query - and run the argument function
  const unsubscribe = onSnapshot(q, async (snapshot) => {
    //snapshot is the set of documents matching the query
    const messagesData = await Promise.all(
      snapshot.docs.map(async (docSnapshot) => {
        //docSnapshot is a single document in the snapshot - i.e. one message
        const message = docSnapshot.data();
        //if message has an author, we want to retun an object that has both the message and author data
        if (message.authorUid) {
          const userData = await fetchPublicUserInfo(message.authorUid);
          if (userData) {
            //object containing message fields, and either their website pfp or google pfp (whichever is true first) as well as the userData
            return {
              ...message,
              messageId: docSnapshot.id,
              authorPhoto: userData.profilePicture,
              authorName: userData.username,
              userInfo: userData,
            };
          }
        }
        //otherwise, no author - just return message
        return message;
      }),
    );

    //update messages of private chat
    setMessages(messagesData);
  });

  //return function to detect changes in the private chat (will remap all the messages if one is added or removed)
  return unsubscribe;
};

/**
 * Sends a message to a private chat.
 * If the input is not empty, the private chat ID exists, and the user is authenticated, the message is sent.
 * The message is added to the private chat's messages collection.
 *
 * @param {string} privateChatId - the ID of the private chat
 * @param {Object} user - the authenticated user object
 * @param {string} input - the message to send
 * @param {Function} setInput - a function to set the input state to an empty string after sending the message
 *
 * @returns {Promise<void>} - a promise that resolves when the message is sent
 */
export const sendPrivateChatMessage = async (
  privateChatId,
  user,
  input,
  setInput,
) => {
  const currentTime = new Date();
  if (input.trim() && privateChatId && user) {
    const messageData = {
      text: input,
      authorUid: user.uid,
      timestamp: currentTime.toISOString(), //need this to sort messages by time
      sentAt: currentTime,
    };
    const messageRef = await addDoc(
      collection(db, "privateChats", privateChatId, "messages"),
      messageData,
    );
    setInput("");

    logWebsiteUsage(
      user.uid,
      `sent message with ID '${messageRef.id}' in private chat with ID '${privateChatId}'`,
      "messageActivity",
      currentTime,
    );
  }
};

/**
 * Deletes a private message from a private chat. Moves to the deletedMessages collection of the private chat.
 *
 * @param {string} privateChatId - the ID of the private chat
 * @param {string} text - the text of the message to delete
 * @param {string} timestamp - the timestamp of the message to delete
 *
 * @returns {Promise<void>} - a promise that resolves when the message is deleted
 */
export const deletePrivateChatMessage = async (privateChatId, messageId) => {
  console.log(
    "Deleting message:",
    messageId,
    "from private chat:",
    privateChatId,
  );

  const messageRef = doc(
    db,
    "privateChats",
    privateChatId,
    "messages",
    messageId,
  );

  try {
    const messageSnapshot = await getDoc(messageRef);
    let message = null;

    if (messageSnapshot.exists()) {
      message = messageSnapshot.data();
      await deleteDoc(messageRef);
    } else {
      console.log("Message not found.");
      return;
    }

    // Move the message to the deletedMessages collection
    const deletedMessagesRef = collection(
      db,
      "privateChats",
      privateChatId,
      "deletedMessages",
    );

    const currentTime = new Date();

    await setDoc(doc(deletedMessagesRef, messageId), {
      ...message,
      chatId: privateChatId,
      deletedAt: currentTime,
    });

    console.log("Message deleted successfully.");
  } catch (error) {
    console.error("Error deleting private chat message:", error);
    return;
  }
};

/**
 * Accepts an invitation to a private chat.
 * The status of the invitation in the user's invitations subcollection is set to 'accepted'.
 * The chat is added to the user's privateChats subcollection
 * The user is added to the private chat members collection.
 * The invitation is updated in the private chat document collection.
 * The members' privateChats subcollection is updated with the membersLastUpdated field.
 *
 * @param {string} invitationId - the ID of the invitation
 * @param {string} chatId - the ID of the private chat
 * @param {Object} user - the authenticated user
 */
export const handleAcceptInvitation = async (invitationId, chatId, user) => {
  console.log("handleAcceptInvitation called");

  const invitationRef = doc(db, "users", user.uid, "invitations", invitationId);
  const invitationSnap = await getDoc(invitationRef);
  const currentTime = new Date();

  if (invitationSnap.exists()) {
    //set invitation status in user doc to be accepted, then add chatId to user's privateChats array
    await updateDoc(invitationRef, {
      status: "accepted",
      acceptedAt: currentTime,
    });

    //update invitation in the privateChat document collection too
    const q = query(
      collection(db, "privateChats", chatId, "invitations"),
      where("userInvitationId", "==", invitationId),
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const invite = querySnapshot.docs[0];
      await updateDoc(invite.ref, { status: "accepted" });
    }

    //add user to private chat members collection (make sure this is set before we trigger the fetchChats() listener to update the private chat list)
    await setDoc(doc(db, "privateChats", chatId, "members", user.uid), {
      username: user.username || "",
      profilePicture: user.profilePicture || "",
      bio: user.bio || "No bio available.",
      membershipStatus: "member", //overwrite status if user was previously a member but left
    });

    //add chatId to user's privateChats array (this triggers the fetchChats() listener to update the private chat list)
    await setDoc(doc(db, "users", user.uid, "privateChats", chatId), {
      settingsLastUpdated: currentTime,
      membersLastUpdated: currentTime, //add membersLastUpdated to track changes in members
      pendingMembersLastUpdated: currentTime, //add pendingMembersLastUpdated to track changes in pending members
    });

    // update members' privateChats subcollection
    const membersRef = collection(db, "privateChats", chatId, "members"); //query for members that are not previous.
    const membersSnap = await getDocs(membersRef);
    const memberUids = membersSnap.docs.map((doc) => doc.id);

    //loop through members and update the matching privateChats subcollection 'membersLastUpdated' field
    await Promise.all(
      memberUids.map(async (memberUid) => {
        //must query for privateChat document where chatId matches

        const memberChatRef = doc(
          db,
          "users",
          memberUid,
          "privateChats",
          chatId,
        );
        /*update the membersLastUpdated field for the member's privateChats subcollection
        also need to update pendingMembersLastUpdated, since this invitation is now accepted - we need to refetch*/
        const memberChatDoc = await getDoc(memberChatRef);
        if (memberChatDoc.exists()) {
          await updateDoc(memberChatRef, {
            membersLastUpdated: currentTime,
            pendingMembersLastUpdated: currentTime,
          });
          console.log(
            `Updated membersLastUpdated for member with UID ${memberUid} in chat ${chatId}`,
          );
        }
      }),
    );

    logWebsiteUsage(
      user.uid,
      `User ACCEPTED invitation with ID '${invitationId}' to join private chat with ID '${chatId}'.`,
      "privateChatInvitationHistory",
      currentTime,
    );

    logWebsiteUsage(
      user.uid,
      `User joined private chat with ID '${chatId}'.`,
      "privateChatMembershipHistory",
      currentTime,
    );

    logPrivateChatActivity(
      chatId,
      `User with ID '${user.uid}' joined the chat. They are now a member.`,
      currentTime,
    );
  } else {
    console.error("Invitation document does not exist.");
  }
};

/**
 * Rejects an invitation to a private chat.
 * The status of the invitation is set to 'rejected'.
 * The invitation is not deleted from the database.
 *
 * @param {string} invitationId - the ID of the invitation
 * @param {string} chatId - the ID of the private chat
 * @param {Object} user - the authenticated user
 *
 * @returns {Promise<void>} - a promise that resolves when the invitation is rejected
 */
export const handleRejectInvitation = async (invitationId, chatId, user) => {
  const invitationRef = doc(db, "users", user.uid, "invitations", invitationId);
  const invitationSnap = await getDoc(invitationRef);
  const currentTime = new Date();

  if (invitationSnap.exists()) {
    await updateDoc(invitationRef, {
      status: "rejected",
      rejectedAt: currentTime,
    });

    const q = query(
      collection(db, "privateChats", chatId, "invitations"),
      where("userInvitationId", "==", invitationId),
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const invite = querySnapshot.docs[0];
      await updateDoc(invite.ref, { status: "rejected" });
    }

    //loop through members and updated their privateChats subcollection pendingMembersLastUpdated field
    const membersRef = collection(db, "privateChats", chatId, "members");
    const membersSnap = await getDocs(membersRef);
    const memberUids = membersSnap.docs.map((doc) => doc.id);

    await Promise.all(
      memberUids.map(async (memberUid) => {
        const memberChatRef = doc(
          db,
          "users",
          memberUid,
          "privateChats",
          chatId,
        );
        const memberChatDoc = await getDoc(memberChatRef);
        if (memberChatDoc.exists()) {
          await updateDoc(memberChatRef, {
            pendingMembersLastUpdated: currentTime,
          });
          console.log(
            `Updated pendingMembersLastUpdated for member with UID ${memberUid} in chat ${chatId}`,
          );
        }
      }),
    );

    logWebsiteUsage(
      user.uid,
      `User DECLINED invitation with ID '${invitationId}' to join private chat with ID '${chatId}'.`,
      "privateChatInvitationHistory",
      currentTime,
    );
  } else {
    console.error("Invitation document does not exist.");
  }
};

/**
 * Cancels an invitation to a private chat.
 * The chatStatus of the invitation is set to 'chatDeleted'.
 * The invitation is not deleted from the database.
 *
 * @param {string} chatId - the ID of the private chat
 * @param {string} userID - the ID of the user whose invitation is being cancelled
 *
 * @returns {Promise<void>} - a promise that resolves when the invitation is cancelled
 */
export const handleCancelInvitation = async (userID, invitationId) => {
  const invitationRef = doc(db, "users", userID, "invitations", invitationId);
  const invitationSnap = await getDoc(invitationRef);
  const currentTime = new Date();

  if (invitationSnap.exists()) {
    await updateDoc(invitationRef, {
      chatStatus: "chatDeleted",
      chatDeletedAt: currentTime,
    });

    //when we call handleCancelInvitation in leavePrivateChat(), we handle updating the private chat invitation collection there
  } else {
    console.error("Invitation document does not exist.");
  }
};

/**
 * Create a new chat with the given chat settings
 * The chat is added to the privateChats collection.
 * Invitations are sent to the invited users.
 * The invitations are added to the users' invitations collection.
 * The private chat document has its own collection of invitations, which records the user ID and status of the invitation.
 *
 * @param {*} chatId, user
 *
 * @param {Object} chatSettings - the settings for the new chat
 * @param {string} chatSettings.name - the name of the chat
 * @param {string} chatSettings.url - the URL of the YouTube video to be associated with the chat
 * @param {Array} chatSettings.invitedUsers - an array of user objects to be invited to the chat
 * @param {Object} user - the authenticated user creating the chat
 * @param {string} user.uid - the UID of the authenticated user
 * @param {string} user.displayName - the display name of the authenticated user
 * @param {string} user.photoURL - the photo URL of the authenticated user
 * @param {string} user.email - the email of the authenticated user
 *
 * @returns {Promise<void>} - a promise that resolves when the chat is created and invitations are sent
 */
export const createChat = async (chatSettings, user) => {
  console.log("in createchat()");
  console.log(chatSettings);

  const currentTime = new Date();

  try {
    const chatData = {
      name: chatSettings.name,
      creator: user.uid,
      url: chatSettings.url,
      icon: null, //icon will be uploaded separately by uploadChatIcon()
      videoTitle: await fetchYoutubeVideoNameFromUrl(chatSettings.url),
      createdAt: currentTime,
    };

    const chatRef = await addDoc(collection(db, "privateChats"), chatData);

    let chatIcon = null;
    if (chatSettings.icon) {
      chatIcon = await uploadChatIcon(chatRef.id, chatSettings.icon);
    }
    console.log("Chat icon uploaded successfully:", chatIcon);

    await updateDoc(chatRef, { icon: chatIcon });
    console.log("Chat icon set successfully");

    // Add owner's invitation
    const ownerInvRef = await addDoc(
      collection(db, "users", user.uid, "invitations"),
      {
        chatId: chatRef.id,
        chatName: chatSettings.name,
        chatIcon: chatIcon,
        invitedBy: user.uid,
        invitedAt: currentTime,
        status: "accepted",
        chatStatus: "active",
        acceptedAt: currentTime, // Owner is automatically accepted
      },
    );

    // Add owner to chat invitations
    await addDoc(collection(db, "privateChats", chatRef.id, "invitations"), {
      userId: user.uid,
      status: "accepted",
      userInvitationId: ownerInvRef.id,
    });

    // Add owner to members collection (use user.uid as key)
    await setDoc(doc(db, "privateChats", chatRef.id, "members", user.uid), {
      username: user.username || "",
      profilePicture: user.profilePicture || "",
      bio: user.bio || "No bio available.",
      membershipStatus: "owner",
    });

    //add chat to user's privateChats array (make sure user is added to members collection before this, so that the fetchChats() listener retrieves the members correctly)
    await setDoc(doc(db, "users", user.uid, "privateChats", chatRef.id), {
      settingsLastUpdated: currentTime,
      membersLastUpdated: currentTime, //add membersLastUpdated to track changes in members
      pendingMembersLastUpdated: currentTime, //add pendingMembersLastUpdated to track changes in pending members
    });

    //only send invitations once chat is set up, to avoid race conditions
    await inviteUsersToPrivateChat(
      chatRef.id,
      chatSettings.name,
      chatIcon,
      user.uid,
      chatSettings.invitedUsers,
      currentTime,
    );

    let chatHistoryMsg = `User created chat with ID '${chatRef.id}'.`;

    if (chatSettings.invitedUsers.length > 0) {
      chatHistoryMsg += " initial invited users: ";
      chatSettings.invitedUsers.forEach((invitedUser) => {
        chatHistoryMsg += `[${invitedUser.uid}], `;
      });
    }

    logWebsiteUsage(
      user.uid,
      chatHistoryMsg,
      "privateChatCreationHistory",
      currentTime,
    );
    logPrivateChatActivity(
      chatRef.id,
      `Chat created by user with ID '${user.uid}'. Chat is now active.`,
      currentTime,
    );

    // Return the chat ID for further use
    return chatRef.id;
  } catch (error) {
    console.error("Error creating chat:", error);
    throw error; // Re-throw so calling code knows it failed
  }
};

/**
 * Fetches the URL of the YouTube video from the Firestore database corresponding to the private chat.
 *
 * @param {string} chatId - the ID of the private chat
 *
 * @returns {Promise<string>} - a promise that resolves to the URL of the YouTube video, or an empty string if the chat does not exist or has no URL
 */
export const fetchPrivateChatVideoUrl = async (chatId) => {
  try {
    const chatRef = doc(db, "privateChats", chatId);
    const chatSnap = await getDoc(chatRef);
    if (chatSnap.exists()) {
      console.log("chatSnap.data().url:", chatSnap.data().url);
      return chatSnap.data().url || "";
    } else {
      console.error("Chat document does not exist.");
      return "";
    }
  } catch (error) {
    console.error("Error fetching chat URL:", error);
    return "";
  }
};

/**
 * Fetches the members of a private chat from the Firestore database
 *
 * @param {*} chatId - the ID of the private chat
 *
 * @returns {Promise<Array>} - a promise that resolves to an array of members
 */
export const fetchPrivateChatMembers = async (chatId) => {
  const memberRef = collection(db, "privateChats", chatId, "members");
  const memberSnap = await getDocs(memberRef);

  let allMembers = [];
  if (!memberSnap.empty) {
    allMembers = memberSnap.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    }));

    return allMembers;
  } else {
    console.error("Chat document does not exist.");
    return [];
  }
};

/**
 * Fetches the members of a private chat who are pending
 *
 * @param {string} chatId - the ID of the private chat
 *
 * @returns {Promise<Array>} - a promise that resolves to an array of pending members
 */
export const fetchPendingInvitations = async (chatId) => {
  const q = query(
    collection(db, "privateChats", chatId, "invitations"),
    where("status", "==", "pending"),
  );

  const chatSnap = await getDocs(q);

  if (!chatSnap.empty) {
    // Get the invited users' data
    const pendingInvitationsData = chatSnap.docs.map((inv) => inv.data());
    return pendingInvitationsData;
  } else {
    //console.error("no pending users");
    return [];
  }
};

/**
 * Fetches the user object with the given userId from the Firestore database.
 *
 * @param {string} userId - the ID of the user to fetch
 *
 * @returns {Promise<Object|null>} - a promise that resolves to the user object if it exists, or null if it does not
 */
export const fetchUser = async (userId) => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return { ...userSnap.data(), uid: userSnap.id };
  } else {
    console.error("User document does not exist.");
    return null;
  }
};

/**
 * Adds the user to the list of invited users in the private chat with chatId.
 * If the user is already in the list, the function does nothing.
 * If the user is not in the list, the function adds the user to the list.
 *
 * @param {string} chatId - the ID of the private chat
 * @param {string} chatName - the name of the private chat
 * @param {string} chatIcon - the icon of the private chat
 * @param {Object} inviter - uid of user sending the invitations
 * @param {Object} invitee - array of user objects to invite
 *
 * @returns {Promise<void>} - a promise that resolves when the user is added to the chat
 */
export const inviteUsersToPrivateChat = async (
  chatId,
  chatName,
  chatIcon,
  inviter,
  invitees,
  currentTime = new Date(),
) => {
  //fetch pending invitations to avoid duplicates
  const pendingInvitations = await fetchPendingInvitations(chatId);
  const members = await fetchPrivateChatMembers(chatId);

  let logMessage = `Invited users to private chat with ID '${chatId}'. invited users: `;

  //only send invitations once chat is set up, to avoid race conditions
  const invitationPromises = invitees.map(async (invitedUser) => {
    if (pendingInvitations.some((inv) => inv.userId === invitedUser.uid)) {
      console.log(
        `User ${invitedUser.uid} is already invited to chat ${chatId}. Skipping invitation.`,
      );
      return; // Skip if user is already invited
    }

    if (members.some((member) => member.uid === invitedUser.uid)) {
      console.log(
        `User ${invitedUser.uid} is already a member of chat ${chatId}. Skipping invitation.`,
      );
      return; // Skip if user is already a member
    }

    //passes invalid checks, so add user to invited list
    logMessage += `[${invitedUser.uid}], `;

    //send invitations to individual user documents
    const invitationData = {
      chatId: chatId,
      chatName: chatName,
      chatIcon: chatIcon,
      invitedBy: inviter,
      invitedAt: currentTime,
      status: "pending",
      chatStatus: "active",
    };

    const invitationRef = await addDoc(
      collection(db, "users", invitedUser.uid, "invitations"),
      invitationData,
    );

    //add invitations to the private chat document invitations
    await addDoc(collection(db, "privateChats", chatId, "invitations"), {
      userId: invitedUser.uid,
      status: "pending",
      userInvitationId: invitationRef.id,
    });
  });

  // Wait for all invitations to complete
  await Promise.all(invitationPromises);

  //loop through members and updated their privateChats subcollection pendingMembersLastUpdated field
  const membersRef = collection(db, "privateChats", chatId, "members");
  const membersSnap = await getDocs(membersRef);
  const memberUids = membersSnap.docs.map((doc) => doc.id);

  await Promise.all(
    memberUids.map(async (memberUid) => {
      const memberChatRef = doc(db, "users", memberUid, "privateChats", chatId);
      const memberChatDoc = await getDoc(memberChatRef);
      if (memberChatDoc.exists()) {
        await updateDoc(memberChatRef, {
          pendingMembersLastUpdated: currentTime,
        });
        console.log(
          `Updated pendingMembersLastUpdated for member with UID ${memberUid} in chat ${chatId}`,
        );
      }
    }),
  );

  logWebsiteUsage(
    inviter,
    logMessage,
    "invitingUsersToPrivateChatHistory",
    currentTime,
  );
};

/**
 * Adds a reaction to a message in a private chat.
 *
 * @param {string} privateChatId - the ID of the private chat
 * @param {string} timestamp - the timestamp of the message to add a reaction to
 * @param {string} reaction - the reaction to add to the message
 *
 * @returns {Promise<void>} - a promise that resolves when the reaction is added
 */
export const managePrivateChatReaction = async (
  userId,
  privateChatId,
  messageId,
  reaction,
) => {
  console.log(
    "userId=",
    userId,
    "privateChatId=",
    privateChatId,
    "messageId=",
    messageId,
    "reaction=",
    reaction,
  );

  const messageRef = doc(
    db,
    "privateChats",
    privateChatId,
    "messages",
    messageId,
  );

  const currentTime = new Date();

  try {
    //if userId doesn't exist in reactions, add it, otherwise, remove it.
    const messageSnap = await getDoc(messageRef);

    if (messageSnap.exists()) {
      const messageData = messageSnap.data();
      console.log("messageData=", messageData);

      //reactionArray may not exist, so we need to use optional chaining
      const reactionArray = messageData?.reactions?.[reaction] || [];

      //user already reacted, so remove their reaction.
      if (reactionArray.includes(userId)) {
        await updateDoc(messageRef, {
          [`reactions.${reaction}`]: arrayRemove(userId), //remove reaction
        });

        logWebsiteUsage(
          userId,
          `removed ${reaction} from message with ID '${messageId}' in private chat with ID '${privateChatId}'.`,
          "messageActivity",
          currentTime,
        );
        //user has not reacted, add their reaction.
      } else {
        await updateDoc(messageRef, {
          [`reactions.${reaction}`]: arrayUnion(userId), //save reaction and count
        });

        logWebsiteUsage(
          userId,
          `reacted with ${reaction} to message with ID '${messageId}' in private chat with ID '${privateChatId}'.`,
          "messageActivity",
          currentTime,
        );
      }
    } else {
      console.error(
        `message ${messageId} from privateChat ${privateChatId} does not exist.`,
      );
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Updates the name of a private chat in the Firestore database.
 * It updates the chat document and also updates the settingsLastUpdated field in each member's privateChats subcollection.
 * This triggers the fetchChats() listener to re-fetch the chat data for all members.
 *
 * @param {Object} selectedChat - the selected private chat object
 * @param {string} selectedChat.id - the ID of the private chat to update
 * @param {string} selectedChat.name - the current name of the private chat
 * @param {string} newName - the new name for the private chat
 */
export const updatePrivateChatName = async (selectedChat, newName) => {
  if (!selectedChat || !newName) {
    console.error("Selected chat or new name is not provided.");
    return;
  }

  //save API, dont re-fetch if the name is the same
  if (selectedChat.name === newName) {
    console.log("New name is the same as the current name, no update needed.");
    return;
  }

  console.log("update private chat name called!");

  try {
    const chatRef = doc(db, "privateChats", selectedChat.id);
    await updateDoc(chatRef, { name: newName });
    console.log("Chat name updated successfully");

    //retrieve members of the chat
    const membersRef = collection(
      db,
      "privateChats",
      selectedChat.id,
      "members",
    );
    const memberSnap = await getDocs(membersRef);
    const memberData = memberSnap.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    }));

    //access members' privateChats subcollection and update settingsLastUpdated field (this triggers their listeners to re-fetch the chat data)
    await Promise.all(
      memberData.map(async (member) => {
        const userChatRef = doc(
          db,
          "users",
          member.uid,
          "privateChats",
          selectedChat.id,
        );
        const userChatSnap = await getDoc(userChatRef);
        if (!userChatSnap.exists()) {
          console.error(
            `User chat document for UID ${member.uid} does not exist.`,
          );
          return;
        }
        //update the settingsLastUpdated field for the member's privateChats subcollection
        await updateDoc(userChatRef, {
          settingsLastUpdated: new Date(),
        });
        console.log(
          `Updated settingsLastUpdated for member with UID ${member.uid} in chat ${selectedChat.id}`,
        );
      }),
    );

    //retrieve pending invitations of chat
    const pendingInvitationsQuery = query(
      collection(db, "privateChats", selectedChat.id, "invitations"),
      where("status", "==", "pending"),
    );

    const pendingInvitationsSnap = await getDocs(pendingInvitationsQuery);
    const pendingInvitations = pendingInvitationsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Update all pending invitations to reflect the new chat name
    await Promise.all(
      pendingInvitations.map(async (invitation) => {
        const invitationRef = doc(
          db,
          "users",
          invitation.userId,
          "invitations",
          invitation.userInvitationId,
        );
        await updateDoc(invitationRef, {
          chatName: newName,
        });
      }),
    );
  } catch (error) {
    console.error("Error updating chat name:", error);
  }
};

/**
 * Updates the URL of a private chat in the Firestore database.
 * It updates the chat document and also updates the lastUpdated field in each member's privateChats subcollection.
 * This triggers the fetchChats() listener to re-fetch the chat data for all members.
 *
 * @param {Object} selectedChat - the selected private chat object
 * @param {string} selectedChat.id - the ID of the private chat to update
 * @param {string} selectedChat.url - the current URL of the private chat
 * @param {string} newUrl - the new URL for the private chat
 */
export const updatePrivateChatUrl = async (selectedChat, newUrl) => {
  if (!selectedChat || !newUrl) {
    console.error("Selected chat or new URL is not provided.");
    return;
  }

  console.log(selectedChat, "newUrl=", newUrl);

  //save API, dont re-fetch if the URL is the same
  if (selectedChat.url === newUrl) {
    console.log("New URL is the same as the current URL, no update needed.");
    return;
  }

  try {
    console.log("id=", selectedChat.id);
    const chatRef = doc(db, "privateChats", selectedChat.id);

    let videoTitle = "no video title found";

    try {
      videoTitle = await fetchYoutubeVideoNameFromUrl(newUrl);
      console.log("videoTitle:", videoTitle);
    } catch (error) {
      console.error("Error fetching video title:", error);
      return;
    }

    await updateDoc(chatRef, { url: newUrl, videoTitle: videoTitle });
    console.log("Chat URL updated successfully");

    //retrieve members of the chat
    const membersRef = collection(
      db,
      "privateChats",
      selectedChat.id,
      "members",
    );
    const memberSnap = await getDocs(membersRef);
    const memberData = memberSnap.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    }));

    //access members' privateChats subcollection and update lastUpdated field (this triggers their listeners to re-fetch the chat data)
    await Promise.all(
      memberData.map(async (member) => {
        const userChatRef = doc(
          db,
          "users",
          member.uid,
          "privateChats",
          selectedChat.id,
        );
        const userChatSnap = await getDoc(userChatRef);
        if (!userChatSnap.exists()) {
          console.error(
            `User chat document for UID ${member.uid} does not exist.`,
          );
          return;
        }
        //update the settingsLastUpdated field for the member's privateChats subcollection
        await updateDoc(userChatRef, {
          settingsLastUpdated: new Date(),
        });
        console.log(
          `Updated settingsLastUpdated for member with UID ${member.uid} in chat ${selectedChat.id}`,
        );
      }),
    );
  } catch (error) {
    console.error("Error updating chat URL:", error);
  }
};

/**
 * Updates the URL of a private chat in the Firestore database.
 * It updates the chat document and also updates the lastUpdated field in each member's privateChats subcollection.
 * This triggers the fetchChats() listener to re-fetch the chat data for all members.
 *
 * @param {Object} selectedChat - the selected private chat object
 * @param {string} selectedChat.id - the ID of the private chat to update
 * @param {string} selectedChat.url - the current URL of the private chat
 * @param {string} newUrl - the new URL for the private chat
 */
export const updatePrivateChatIcon = async (selectedChat, selectedFile) => {
  console.log("updatePrivateChatIcon called with:", {
    selectedChat,
    selectedFile,
  });

  if (!selectedChat) {
    //its okay if selectedFile is null, because UI will render the default icon
    console.error("Selected chat or new icon file is not provided.");
    return;
  }

  //save API, dont re-fetch if the icon is the same
  if (selectedChat.icon === selectedFile) {
    console.log("New icon is the same as the current icon, no update needed.");
    return;
  }

  try {
    const chatRef = doc(db, "privateChats", selectedChat.id);

    let chatIcon = null;
    if (selectedFile) {
      chatIcon = await uploadChatIcon(selectedChat.id, selectedFile);
    }
    console.log("Chat icon uploaded successfully:", chatIcon);

    await updateDoc(chatRef, { icon: chatIcon });
    console.log("Chat icon updated successfully");

    //retrieve members of the chat
    const membersRef = collection(
      db,
      "privateChats",
      selectedChat.id,
      "members",
    );
    const memberSnap = await getDocs(membersRef);
    const memberData = memberSnap.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    }));

    //access members' privateChats subcollection and update settingsLastUpdated field (this triggers their listeners to re-fetch the chat data)
    await Promise.all(
      memberData.map(async (member) => {
        const userChatRef = doc(
          db,
          "users",
          member.uid,
          "privateChats",
          selectedChat.id,
        );
        const userChatSnap = await getDoc(userChatRef);
        if (!userChatSnap.exists()) {
          console.error(
            `User chat document for UID ${member.uid} does not exist.`,
          );
          return;
        }
        //update the settingsLastUpdated field for the member's privateChats subcollection
        await updateDoc(userChatRef, {
          settingsLastUpdated: new Date(),
        });
        console.log(
          `Updated settingsLastUpdated for member with UID ${member.uid} in chat ${selectedChat.id}`,
        );
      }),
    );

    const pendingInvitationsQuery = query(
      collection(db, "privateChats", selectedChat.id, "invitations"),
      where("status", "==", "pending"),
    );

    const pendingInvitationsSnap = await getDocs(pendingInvitationsQuery);
    const pendingInvitations = pendingInvitationsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Update all pending invitations to reflect the new chat name
    await Promise.all(
      pendingInvitations.map(async (invitation) => {
        const invitationRef = doc(
          db,
          "users",
          invitation.userId,
          "invitations",
          invitation.userInvitationId,
        );
        await updateDoc(invitationRef, {
          chatIcon: chatIcon,
        });
      }),
    );
  } catch (error) {
    console.error("Error updating chat icon:", error);
  }
};

/**
 * Function to upload a chat icon to Firebase Storage and update the chat document in Firestore.
 *
 * @param {string} chatId - The ID of the private chat where the chat icon is being uploaded
 * @param {File} file - The file object representing the chat icon to be uploaded
 *
 * @returns {Promise<string>} A promise that resolves to the public URL of the uploaded chat icon.
 * @throws {Error} If there is an error uploading the chat icon or updating the chat document.
 */
export const uploadChatIcon = async (chatId, file) => {
  try {
    // Create a reference to the storage bucket location, and get the reference
    const storageRef = ref(storage, `chatIcons/${chatId}`);
    const contentType = getContentType(file); // Determine the content type
    const metadata = {
      contentType: contentType,
    };

    //upload file and metadata to storage at storageRef. snapshot stores the metadata and reference to uploaded file
    const snapshot = await uploadBytes(storageRef, file, metadata);
    const chatIcon = await getDownloadURL(snapshot.ref); //get a public URL for the file based on the snapshot reference - chatIcon can be used to display img

    return chatIcon;
  } catch (error) {
    console.error("Error uploading chat icon:", error);
    throw error;
  }
};

/**
 * Function to log private chat activity (members joining/leaving, transfer of ownership) in the private chat subcollection
 *
 * NOTE: we choose not to await addDoc because it doesnt need to synchronous.
 *
 * @param {string} userId - The ID of the user involved in the activity
 * @param {string} action - A description of the activity being logged
 * @param {Date} currentTime - The current timestamp when the activity is logged (optional)
 */
export const logPrivateChatActivity = async (
  privateChatId,
  action,
  currentTime = new Date(),
) => {
  const activityRef = collection(
    db,
    "privateChats",
    privateChatId,
    "privateChatActivity",
  );

  // Log the activity in Firestore
  addDoc(activityRef, {
    action: action,
    timestamp: currentTime,
  });
};

/**
 * Function to log private chat activity to deleted private chat
 *
 * @param {string} userId - The ID of the user involved in the activity
 * @param {string} action - A description of the activity being logged
 * @param {Date} currentTime - The current timestamp when the activity is logged (optional)
 */
export const logDeletedPrivateChatActivity = (
  privateChatId,
  action,
  currentTime = new Date(),
) => {
  const activityRef = collection(
    db,
    "deletedPrivateChats",
    privateChatId,
    "privateChatActivity",
  );

  // Log the activity in Firestore
  addDoc(activityRef, {
    action: action,
    timestamp: currentTime,
  });
};
